import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Mail, Paperclip, Pencil, Trash2, Undo2, X } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/format";
import { useInitData } from "@/lib/init-data";
import {
  approveAmount,
  declineAmount,
  deleteRequest,
  downloadAttachmentBlob,
  fetchRequestDetail,
  formatAmount,
  isInternalTransferType,
  sendApprovalNotification,
  statusMeta,
  undoApproveAmount,
  type PaymentRequestAmountLine,
  type PaymentRequestDetail,
} from "@/lib/payreq";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [req, setReq] = useState<PaymentRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!token || !id) return;
      setLoading((l) => l && true);
      fetchRequestDetail(token, id, signal)
        .then((d) => setReq(d))
        .catch((e) => {
          if ((e as Error | undefined)?.name === "AbortError") return;
          toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Could not load the request.");
        })
        .finally(() => setLoading(false));
    },
    [token, id]
  );
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const onDelete = () => {
    if (!token || !req) return;
    setDeleting(true);
    deleteRequest(token, req.id)
      .then((r) => {
        toast.success(r.myMessage || "Request deleted.");
        navigate("/requests", { replace: true });
      })
      .catch((e) => {
        toast.error(
          e instanceof ApiError && e.status === 403
            ? "This request can no longer be deleted."
            : e instanceof Error
              ? e.message
              : "Could not delete the request."
        );
      })
      .finally(() => setDeleting(false));
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Title + back arrow now live in the shell's app bar. */}
      {loading ? (
        <DetailSkeleton />
      ) : !req ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          This request could not be loaded.
        </div>
      ) : (
        <>
          <SummaryCard req={req} />
          <TimelineCard req={req} />
          <AmountLinesCard req={req} token={token} onChanged={() => load()} />
          {(req.btnEdit || req.btnDelete) && (
            <div className="flex gap-2">
              {req.btnEdit && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate(`/requests/${req.id}/edit`)}
                >
                  <Pencil /> Edit
                </Button>
              )}
              {req.btnDelete && (
                <ConfirmDialog
                  trigger={
                    <Button variant="destructive" className="flex-1" disabled={deleting}>
                      <Trash2 /> {deleting ? "Deleting…" : "Delete"}
                    </Button>
                  }
                  title="Delete this request?"
                  description={`${req.receiptNumber || "This request"} will be deleted. This cannot be undone from the app.`}
                  confirmText="Delete"
                  destructive
                  onConfirm={onDelete}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ req }: { req: PaymentRequestDetail }) {
  const meta = statusMeta(req.status);
  const { reqTypeInternalTransferId } = useInitData();
  const internal = isInternalTransferType(req.reqType, reqTypeInternalTransferId);
  const transferFrom = req.paymentRequestTransferFrom;
  return (
    <Card size="sm">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-mono text-sm">{req.receiptNumber || "—"}</CardTitle>
          <div className="flex items-center gap-1.5">
            {internal && <Badge variant="secondary">Internal transfer</Badge>}
            <Badge variant="outline" className={cn("capitalize", meta.className)}>
              {meta.label}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        <Row label="Date" value={formatDate(req.reqDate)} />
        {/* Vendor / mode of payment do not apply to internal transfers (server nulls them). */}
        {!internal && req.vendor?.name && <Row label="Vendor" value={req.vendor.name} />}
        {!internal && req.modeOfPayment?.label && (
          <Row label="Mode of payment" value={req.modeOfPayment.label} />
        )}
        {internal && transferFrom && (
          <>
            <Row
              label="Transfer from"
              value={
                [
                  transferFrom.expenseBudget?.name,
                  transferFrom.department?.name ?? transferFrom.expenseBudget?.departmentName,
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"
              }
            />
            <Row label="Transfer amount" value={formatAmount(transferFrom.amount)} />
          </>
        )}
        {req.priority?.label && <Row label="Priority" value={req.priority.label} />}
        {req.reqNote && <Row label="Note" value={req.reqNote} />}
        <Separator className="my-1" />
        <Row label="Requested" value={formatAmount(req.amountReq)} strong />
        <Row label="Paid" value={formatAmount(req.amountPaid)} />
        <Row label="Balance" value={formatAmount(req.amountBalance)} />
      </CardContent>
    </Card>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("text-right", strong && "font-semibold")}>{value}</span>
    </div>
  );
}

interface TimelineStep {
  label: string;
  reached: boolean;
  date?: string | null;
  detail?: string | null;
  alert?: boolean;
}

function timelineSteps(req: PaymentRequestDetail): TimelineStep[] {
  const status = req.status ?? "";
  const lines = req.listPaymentRequestAmount ?? [];
  const approvedByStatus = ["APPROVED", "PROCESSING", "ON_HOLD", "PARTIALLY_PAID", "PAID"].includes(status);
  const approvedByLines = lines.length > 0 && lines.every((l) => !!l.reqApproved);
  const approvedDate = lines
    .map((l) => l.reqApprovedDate)
    .filter(Boolean)
    .sort()
    .pop();
  const paid = !!req.paid || status === "PAID";

  const steps: TimelineStep[] = [
    { label: "Raised", reached: true, date: req.reqDate },
    { label: "Approved", reached: approvedByStatus || approvedByLines, date: approvedDate },
    { label: "Accepted", reached: !!req.accepted, date: req.acceptedDate },
  ];
  if (req.onHold) {
    steps.push({ label: "On hold", reached: true, detail: req.onHoldReason, alert: true });
  }
  steps.push({ label: "Paid", reached: paid, date: req.paidDate });
  return steps;
}

function TimelineCard({ req }: { req: PaymentRequestDetail }) {
  const steps = timelineSteps(req);
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Progress</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col">
          {steps.map((s, i) => (
            <li key={s.label} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px]",
                    s.reached
                      ? s.alert
                        ? "border-red-500 bg-red-500 text-white"
                        : "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-muted text-muted-foreground"
                  )}
                >
                  {s.reached ? <Check className="size-3" /> : null}
                </span>
                {i < steps.length - 1 && (
                  <span className={cn("w-px flex-1 min-h-5", s.reached ? "bg-primary/50" : "bg-border")} />
                )}
              </div>
              <div className="flex flex-col pb-4">
                <span
                  className={cn(
                    "text-sm font-medium",
                    !s.reached && "text-muted-foreground",
                    s.alert && "text-red-600 dark:text-red-400"
                  )}
                >
                  {s.label}
                </span>
                {s.date && <span className="text-xs text-muted-foreground">{formatDate(s.date)}</span>}
                {s.detail && <span className="text-xs text-muted-foreground">{s.detail}</span>}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function AmountLinesCard({
  req,
  token,
  onChanged,
}: {
  req: PaymentRequestDetail;
  token: string | null;
  onChanged: () => void;
}) {
  const lines = req.listPaymentRequestAmount ?? [];
  if (lines.length === 0) return null;
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Amount lines</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {lines.map((l, i) => (
          <AmountLine key={l.id ?? i} line={l} first={i === 0} token={token} canResend={!!req.btnEdit} onChanged={onChanged} />
        ))}
      </CardContent>
    </Card>
  );
}

function AmountLine({
  line: l,
  first,
  token,
  canResend,
  onChanged,
}: {
  line: PaymentRequestAmountLine;
  first: boolean;
  token: string | null;
  canResend: boolean;
  onChanged: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [acting, setActing] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  // Shared runner for approve / undo / decline: surface server errors, refresh the page state.
  const runAction = (fn: () => Promise<{ myMessage?: string; errors?: string; errorMessage?: string }>) => {
    setActing(true);
    fn()
      .then((r) => {
        const err = r.errorMessage || r.errors;
        if (err) {
          toast.error(err);
          return;
        }
        toast.success(r.myMessage || "Done.");
        setDeclineOpen(false);
        setDeclineReason("");
        onChanged();
      })
      .catch((e) => {
        toast.error(
          e instanceof ApiError && e.status === 403
            ? "You can no longer act on this line."
            : e instanceof Error
              ? e.message
              : "The action failed."
        );
      })
      .finally(() => setActing(false));
  };

  const resend = () => {
    if (!token || !l.id) return;
    setSending(true);
    sendApprovalNotification(token, l.id)
      .then((r) => {
        if (r.errorMessage) toast.error(r.errorMessage);
        else toast.success(r.myMessage || "Approval email sent.");
      })
      .catch((e) => {
        toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Could not send the email.");
      })
      .finally(() => setSending(false));
  };

  return (
    <div className="flex flex-col gap-1">
      {!first && <Separator className="mb-2" />}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{formatAmount(l.amount)}</span>
        {l.reqApproved ? (
          <Badge
            variant="outline"
            className="border-green-300 bg-green-100 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200"
          >
            Approved
          </Badge>
        ) : l.reqApproved === false ? (
          <Badge
            variant="outline"
            className="border-red-300 bg-red-100 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
          >
            Declined
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
          >
            Awaiting approval
          </Badge>
        )}
      </div>
      {(l.expenseBudget?.name || l.department?.name) && (
        <div className="text-xs text-muted-foreground">
          {[l.expenseBudget?.name, l.expenseBudget?.departmentName || l.department?.name]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}
      {l.remarks && <div className="text-xs">{l.remarks}</div>}
      {l.reqApproved && l.reqApprovedBy?.displayNameWithTitle && (
        <div className="text-xs text-muted-foreground">
          Approved by {l.reqApprovedBy.displayNameWithTitle}
          {l.reqApprovedDate ? ` on ${formatDate(l.reqApprovedDate)}` : ""}
        </div>
      )}
      {l.reqApproved === false && (
        <div className="text-xs text-red-700 dark:text-red-300">
          Declined{l.reqApprovedBy?.displayNameWithTitle ? ` by ${l.reqApprovedBy.displayNameWithTitle}` : ""}
          {l.reqApprovedNote ? ` — ${l.reqApprovedNote}` : ""}
        </div>
      )}
      {l.reqApproved == null && canResend && (
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="mt-1 self-start"
          disabled={sending}
          onClick={resend}
        >
          {sending ? <Loader2 className="animate-spin" /> : <Mail />}
          Resend approval email
        </Button>
      )}
      {(l.btnApprove || l.btnRevertApproval) && (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {l.btnApprove && (
            <>
              <Button
                type="button"
                size="sm"
                disabled={acting || !token || !l.id}
                onClick={() => l.id && token && runAction(() => approveAmount(token, l.id))}
              >
                {acting ? <Loader2 className="animate-spin" /> : <Check />} Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={acting || !token || !l.id}
                onClick={() => setDeclineOpen(true)}
              >
                <X /> Decline
              </Button>
            </>
          )}
          {l.btnRevertApproval && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={acting || !token || !l.id}
              onClick={() => l.id && token && runAction(() => undoApproveAmount(token, l.id))}
            >
              {acting ? <Loader2 className="animate-spin" /> : <Undo2 />} Undo approval
            </Button>
          )}
        </div>
      )}
      <Dialog open={declineOpen} onOpenChange={(o) => !acting && setDeclineOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Decline this amount?</DialogTitle>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`decline-reason-${l.id}`}>Reason (sent to the requester)</Label>
            <Textarea
              id={`decline-reason-${l.id}`}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Why is this being declined?"
              rows={3}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={acting} onClick={() => setDeclineOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={acting || !declineReason.trim() || !token || !l.id}
              onClick={() => l.id && token && runAction(() => declineAmount(token, l.id, declineReason))}
            >
              {acting ? "Declining…" : "Decline"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {(l.listPaymentRequestAttachment ?? []).length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5">
          {(l.listPaymentRequestAttachment ?? []).map((a) => (
            <AttachmentRow key={a.id} id={a.id} filePath={a.filePath} token={token} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Tappable attachment: fetches the file with the auth header, then opens it as a blob URL. */
function AttachmentRow({
  id,
  filePath,
  token,
}: {
  id: number;
  filePath: string | null;
  token: string | null;
}) {
  const [fetching, setFetching] = useState(false);

  const view = useCallback(() => {
    if (!token || fetching) return;
    setFetching(true);
    downloadAttachmentBlob(token, id)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const opened = window.open(url, "_blank");
        if (!opened) {
          // Popup blocked — fall back to a programmatic anchor click.
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        // Give the new tab time to load before releasing the blob.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) => {
        toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Could not open the attachment.");
      })
      .finally(() => setFetching(false));
  }, [token, id, fetching]);

  return (
    <li>
      <button
        type="button"
        onClick={view}
        disabled={fetching}
        className="flex max-w-full items-center gap-1 text-xs text-primary underline-offset-2 hover:underline disabled:opacity-60"
      >
        {fetching ? (
          <Loader2 className="size-3 shrink-0 animate-spin" />
        ) : (
          <Paperclip className="size-3 shrink-0" />
        )}
        <span className="truncate">{filePath || "(attachment)"}</span>
      </button>
    </li>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card size="sm" key={i}>
          <CardContent className="flex flex-col gap-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-3/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
